import { icon_microphone_r, icon_microphone_w, icon_notice_g } from 'components/base/images';
import WhiteButton from 'components/common/button/WhiteButton';
import CustomEditor from 'components/common/input/CustomEditor';
import DotSpinner from 'components/common/spinner/DotSpinner';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import { useCreateProjectMemo } from 'lib/hooks/project/useCreateProjectMemo';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import cx from 'classnames';
import { CircularProgress } from '@material-ui/core';
import { mapRange } from 'lib/clinic/library';
import { useShallowAppSelector } from 'store/hooks';

const Recording = () => {
  const { loadedSource } = useShallowAppSelector(state => ({
    loadedSource: state.app.loadedSource,
  }));
  const listeningRef = useRef<HTMLDivElement>(null);
  const {
    isRecording,
    isTranscriptionLoading,
    recordingTime,
    timeProgress,
    decibel,
    senderMemo,
    isMemoEdit,
    isMemoInit,
    isCreatedOnlineProject,
    onMemoChange,
    onMicrophoneClick,
    onEditMemo,
    onSaveMemo,
    onCancelMemo,
  } = useCreateProjectMemo();
  const { t } = useTranslation();

  return (
    <StyledRecording>
      <div className="recording__content_container">
        <div className="recording__input_box">
          {isMemoInit && (
            <CustomEditor
              value={senderMemo}
              className="memo_input_field"
              // disabled={!isMemoEdit}
              onChange={onMemoChange}
              placeholder={t('RECORD_PLACEHOLER')}
              maxLength={500}
            >
              {isRecording && (
                <div ref={listeningRef} className="record_listening_box">
                  <div
                    style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#D85066' }}
                  />
                  <div className="listening_content">
                    <CustomText fontSize={14} fontColor="#B2B6B8">
                      <T>RECORD_LISTENING</T>
                    </CustomText>
                    <DotSpinner color="#B2B6B8" size={4} gap={2} />
                  </div>
                </div>
              )}
            </CustomEditor>
          )}
          <div className="recording__information_item">
            <img src={icon_notice_g} />
            <CustomText fontSize={14} fontColor="#888" fontWeight={400}>
              <T>RECORD_INFORMATION</T>
            </CustomText>
          </div>
        </div>
        <div className="recording__record_button_box">
          {isRecording && (
            <>
              <div className="recording__record_time">
                <CustomText fontSize={14} fontColor="#D85066" fontWeight={400}>
                  {recordingTime}
                </CustomText>
              </div>
              <CircularProgress
                className="recording__record_progress"
                variant="determinate"
                size={92}
                value={100 - timeProgress}
                thickness={1.5}
                style={{ color: '#D85066' }}
              />
              <StyledRecordDecibel $size={mapRange(decibel, 10, 50, 80, 140)} />
            </>
          )}
          <button
            className={cx(`recording__record_button`, { isRecording: isRecording })}
            onClick={() => onMicrophoneClick(isRecording ? 'stop' : 'start')}
            disabled={loadedSource === 'local'}
          >
            {isRecording ? (
              <div className="stop_icon" />
            ) : (
              <img
                className={cx(`record_icon`, { isMemoEdit: isMemoEdit })}
                src={icon_microphone_r}
                alt="icon_microphone_r"
              />
            )}
          </button>
        </div>
      </div>
      <div className="recording__button_container">
        <div className="recording__content_button_box">
          {isMemoEdit && (
            <WhiteButton
              width={156}
              height={40}
              borderRadius={20}
              onClick={onCancelMemo}
              disabled={isRecording}
            >
              <CustomText fontSize={18} fontWeight={400}>
                <T>GLOBAL_CANCEL</T>
              </CustomText>
            </WhiteButton>
          )}
          {isMemoEdit && (
            <WhiteButton
              width={156}
              height={40}
              borderRadius={20}
              onClick={onSaveMemo}
              disabled={isRecording}
            >
              <CustomText fontSize={18} fontWeight={400}>
                <T>GLOBAL_SAVE</T>
              </CustomText>
            </WhiteButton>
          )}
          {/* {!isMemoEdit && (
            <WhiteButton
              width={100}
              height={34}
              borderRadius={50}
              onClick={onEditMemo}
              disabled={isRecording || isCreatedOnlineProject}
            >
              <CustomText fontSize={14} fontWeight={400}>
                <T>GLOBAL_EDIT</T>
              </CustomText>
            </WhiteButton>
          )} */}
        </div>
      </div>
    </StyledRecording>
  );
};

const StyledRecording = styled.div`
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  .recording__content_container {
    flex: 1;
    .recording__input_box {
      display: flex;
      flex-direction: column;
      row-gap: 5px;
      margin-bottom: 25px;
      .memo_input_field {
        height: 200px;
        border: none;
        background-color: #fafafa;
        padding: 16px;
        font-size: 16px;
        color: #444;
        font-weight: 400;
        line-height: 1.5;
        white-space: pre-wrap; /* 엔터나 띄어쓰기를 유지하기 위함 */
        word-wrap: break-word; /* 줄바꿈 자동처리 */
        overflow: auto;
      }
      .recording__information_item {
        display: flex;
        align-items: center;
        column-gap: 5px;
      }
      .record_listening_box {
        display: flex;
        width: 100%;
        height: 34px;
        background-color: #fff;
        margin-top: 12px;
        align-items: center;
        border-radius: 8px;
        padding: 12px;
        column-gap: 8px;
        .listening_content {
          display: flex;
          column-gap: 5px;
        }
      }
    }
    .recording__record_button_box {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 20px;
      margin-bottom: 20px;
      position: relative;
      .recording__record_progress {
        position: absolute;
      }
      .recording__record_time {
        position: absolute;
        top: 0;
      }
      .recording__record_button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 80px;
        height: 80px;
        border: 1px solid #d1d1d1;
        border-radius: 50%;
        z-index: 1;
        &:disabled {
          background-color: #ced2df;
        }
        &.isRecording {
          background-color: #d85066;
          border: none;
        }
        .stop_icon {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          border: 3px solid #fff;
        }
        .record_icon {
          &:not(&.isMemoEdit) {
            opacity: 0.5;
          }
        }
      }
    }
  }
  .recording__button_container {
    .recording__content_button_box {
      display: flex;
      justify-content: space-between;
      column-gap: 10px;
      width: 100%;
      height: 34px;
    }
  }
`;

const StyledRecordDecibel = styled.div<{ $size?: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  position: absolute;
  border-radius: 50%;
  background-color: rgba(216, 80, 102, 0.1);
  /* 0.2초 동안 width와 height가 부드럽게 변화하도록 설정 */
  transition: width 0.2s ease, height 0.2s ease;
`;

export default Recording;
